<template>
  <div
    class="group bg-white dark:bg-gray-800/50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
  >
    <!-- Cover Image -->
    <div class="h-48 relative overflow-hidden bg-gray-50 dark:bg-gray-900/50">
      <template v-if="event.attributes.cover?.data?.attributes?.url">
        <!-- Background blur image -->
        <img
          :src="event.attributes.cover.data.attributes.url"
          :alt="event.attributes.title"
          class="absolute inset-0 w-full h-full object-cover blur-sm opacity-60"
        />
        <!-- Main centered image -->
        <img
          :src="event.attributes.cover.data.attributes.url"
          :alt="event.attributes.title"
          class="relative z-10 w-full h-full object-contain p-2 transition-all duration-300 group-hover:scale-105"
        />
      </template>
      <div v-else class="w-full h-full flex items-center justify-center">
        <Icon
          name="heroicons:photo"
          class="w-16 h-16 text-gray-300 dark:text-gray-600"
        />
      </div>
    </div>

    <div class="p-6 space-y-4">
      <!-- Status and Category -->
      <div class="flex items-center justify-between gap-2">
        <span
          class="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full dark:bg-primary/20 dark:text-primary-light inline-flex items-center gap-1"
        >
          <Icon name="heroicons:folder" class="w-3 h-3" />
          {{ event.attributes.aa_category.data?.attributes.title }}
        </span>
      </div>

      <!-- Title -->
      <h3
        class="text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary transition-colors line-clamp-2"
      >
        {{ event.attributes.title }}
        <Icon
          v-if="event.attributes.isPublished"
          name="heroicons:check-badge"
          class="inline-block w-5 h-5 text-blue-500 flex-shrink-0 ml-1 -mt-1"
        />
      </h3>

      <!-- Date Range with improved visual -->
      <div
        class="flex items-center text-gray-600 dark:text-gray-300 text-sm bg-gray-50 dark:bg-gray-800/50 p-2 rounded-lg"
      >
        <Icon name="heroicons:calendar" class="w-4 h-4 mr-2 text-primary" />
        <span>
          <NuxtTime :datetime="event.attributes.starts" format="MMMM D, YYYY" />
          <template v-if="event.attributes.starts !== event.attributes.ends">
            <span class="mx-2">→</span>
            <NuxtTime :datetime="event.attributes.ends" format="MMMM D, YYYY" />
          </template>
        </span>
      </div>

      <!-- Description -->
      <MDC
        class="text-gray-600 dark:text-gray-300 line-clamp-2 text-sm"
        :value="event.attributes.description"
      />

      <!-- Tags -->
      <div class="flex flex-wrap gap-1.5">
        <span
          v-for="tag in event.attributes.aa_tags.data"
          :key="tag.id"
          class="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          # {{ tag.attributes.title }}
        </span>
      </div>

      <!-- Action Button -->
      <nuxt-link
        :to="'/events/' + event.attributes.slug"
        class="mt-4 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 focus:ring-4 focus:ring-primary/30 transition-all w-full group/btn"
      >
        View Details
        <Icon
          name="heroicons:arrow-right"
          class="w-4 h-4 ml-2 transform group-hover/btn:translate-x-1 transition-transform"
        />
      </nuxt-link>
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  event: {
    id: number;
    attributes: {
      title: string;
      description: string;
      slug: string;
      starts: string;
      ends: string;
      isPublished: boolean;
      cover?: {
        data?: {
          attributes?: {
            url: string;
          };
        };
      };
      aa_category: {
        data: {
          attributes: {
            title: string;
          };
        };
      };
      aa_tags: {
        data: Array<{
          id: number;
          attributes: {
            title: string;
          };
        }>;
      };
    };
  };
}>();
</script>
